import { NgModule } from '@angular/core';
import { Route, RouterModule, Routes } from '@angular/router';

import { AuthenticatorService } from '@aws-amplify/ui-angular';

import { AgeUnverifiedGuard } from '@age-verification/guards/age-unverified.guard';
import { AgeVerifiedGuard } from '@age-verification/guards/age-verified.guard';
import { AuthenticatedGuard } from '@authentication/guards/authenticated.guard';
import { UserRoleGuard } from '@authentication/guards/telesales.guard copy';
import { LayoutContainerComponent } from '@layout/containers/layout-container/layout-container.component';
import { ProductsModule } from '@products/products.module';
import { DevModeGuard } from '@shared/guards/dev-mode.guard';

import { appInjector } from '../main';
import { UserAdminGuard } from './merchant-account/guards/user-admin.guard';

const layoutRoutes: Route[] = [
  {
    path: 'admin',
    canActivateChild: [AuthenticatedGuard],
    canActivate: [UserRoleGuard],
    data: {
      acceptedRoles: ['EASYLOT_ADMIN', 'IMPERSONATION'],
    },
    loadChildren: () =>
      import('./easylot-admin/easylot-admin.module').then(
        (m) => m.EasylotAdminModule
      ),
  },
  {
    path: 'account/users',
    canActivateChild: [AuthenticatedGuard, UserAdminGuard],
    loadChildren: () =>
      import('./merchant-account/merchant-account.module').then(
        (m) => m.MerchantAccountModule
      ),
  },
  {
    path: 'auth',
    loadChildren: () =>
      import('./authentication/authentication.module').then(
        (m) => m.AuthenticationModule
      ),
  },
  {
    path: 'contact',
    data: { hasBanner: true },
    loadChildren: () =>
      import('./contact/contact.module').then((m) => m.ContactModule),
  },
  {
    path: 'selling/containers',
    canActivateChild: [AuthenticatedGuard],
    loadChildren: () =>
      import('./containers/containers.module').then((m) => m.ContainersModule),
  },
  {
    path: 'blog',
    canActivateChild: [AgeVerifiedGuard],
    loadChildren: () => import('./blog/blog.module').then((m) => m.BlogModule),
  },
  {
    path: 'selling/delivery-mode',
    canActivateChild: [AuthenticatedGuard],
    loadChildren: () =>
      import('./delivery-mode/delivery-mode.module').then(
        (m) => m.DeliveryModeModule
      ),
  },
  {
    path: 'founders',
    data: { hasBanner: true },
    loadChildren: () =>
      import('./founders/founders.module').then((m) => m.FoundersModule),
  },
  {
    path: 'home',
    data: { hasBanner: true },
    loadChildren: () => import('./home/home.module').then((m) => m.HomeModule),
  },
  {
    path: 'buying',
    canActivateChild: [AuthenticatedGuard],
    loadChildren: () =>
      import('./buying/buying.module').then((m) => m.BuyingModule),
  },
  {
    path: 'selling',
    canActivateChild: [AuthenticatedGuard],
    loadChildren: () =>
      import('./selling/selling.module').then((m) => m.SellingModule),
  },
  {
    path: 'account',
    canActivateChild: [AuthenticatedGuard],
    loadChildren: () =>
      import('./account/account.module').then((m) => m.AccountModule),
  },
  {
    path: 'account/merchant',
    canActivateChild: [AuthenticatedGuard],
    loadChildren: () =>
      import('./merchant/merchant.module').then((m) => m.MerchantModule),
  },
  {
    path: 'modules',
    data: { hasBanner: true },
    canActivateChild: [AgeVerifiedGuard],
    loadChildren: () =>
      import('./modules/modules.module').then((m) => m.ModulesModule),
  },
  {
    path: 'buying/order-list',
    canActivateChild: [AgeVerifiedGuard, AuthenticatedGuard],
    loadChildren: () =>
      import('./order-list/order-list.module').then((m) => m.OrderListModule),
  },
  {
    path: 'prices',
    data: { hasBanner: true },
    loadChildren: () =>
      import('./module-prices/module-prices.module').then(
        (m) => m.ModulePricesModule
      ),
  },
  {
    path: 'selling/prices/pricing',
    data: { sidenavForceOver: true },
    canActivateChild: [AuthenticatedGuard],
    loadChildren: () =>
      import('./pricing/pricing.module').then((m) => m.PricingModule),
  },
  {
    path: 'selling/product-import',
    canActivateChild: [AuthenticatedGuard],
    loadChildren: (): Promise<typeof ProductsModule> =>
      import('./product-import/product-import.module').then(
        (m) => m.ProductImportModule
      ),
  },
  {
    path: 'selling/purchase-lists',
    canActivateChild: [AuthenticatedGuard],
    loadChildren: () =>
      import(
        './purchase-lists/modules/purchase-lists-incoming/purchase-list-incoming.module'
      ).then((m) => m.PurchaseListIncomingModule),
  },
  {
    path: 'buying/purchase-lists',
    canActivateChild: [AuthenticatedGuard],
    loadChildren: () =>
      import(
        './purchase-lists/modules/purchase-lists-outgoing/purchase-list-outgoing.module'
      ).then((m) => m.PurchaseListOutgoingModule),
  },
  {
    path: 'buying/search',
    data: { sidenavForceOver: true },
    canActivateChild: [AuthenticatedGuard],
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./search/search.module').then((m) => m.SearchModule),
      },
    ],
  },
  {
    path: 'selling/search',
    data: { sidenavForceOver: true },
    canActivateChild: [AuthenticatedGuard],
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./search/search.module').then((m) => m.SearchModule),
      },
    ],
  },
  {
    path: 'selling/sortiment',
    data: { sidenavForceOver: true },
    canActivateChild: [AuthenticatedGuard],
    loadChildren: () =>
      import('./sortiment/sortiment.module').then((m) => m.SortimentModule),
  },
  {
    path: 'buying/sortiment',
    data: { sidenavForceOver: true },
    canActivateChild: [AuthenticatedGuard],
    loadChildren: () =>
      import('./sortiment/sortiment.module').then((m) => m.SortimentModule),
  },
  {
    path: 'telesales',
    canActivateChild: [AuthenticatedGuard],
    canActivate: [UserRoleGuard],
    data: {
      acceptedRoles: ['TELESALES'],
    },
    loadChildren: () =>
      import('./telesales/telesales.module').then((m) => m.TelesalesModule),
  },
  {
    path: 'selling/tenders',
    canActivateChild: [AuthenticatedGuard],
    loadChildren: () =>
      import('./tender/tender.module').then((m) => m.TenderModule),
  },
  {
    path: 'buying/vip-discounts',
    canActivateChild: [AuthenticatedGuard],
    loadChildren: () =>
      import(
        './vip-discounts/modules/vip-discounts-buying/vip-discounts-buying.module'
      ).then((m) => m.VipDiscountsBuyingModule),
  },
  {
    path: 'selling/prices/discounts/vip-discounts',
    data: { sidenavForceOver: true },
    canActivateChild: [AuthenticatedGuard],
    loadChildren: () =>
      import(
        './vip-discounts/modules/vip-discounts-selling/vip-discounts-selling.module'
      ).then((m) => m.VipDiscountsSellingModule),
  },
  {
    path: 'selling/prices/discounts/segment-discounts',
    canActivateChild: [AuthenticatedGuard],
    loadChildren: () =>
      import('./segment-discounts/segment-discounts.module').then(
        (m) => m.SegmentDiscountsModule
      ),
  },
  {
    path: 'selling/prices/discounts/basket-discounts',
    canActivateChild: [AuthenticatedGuard],
    loadChildren: () =>
      import('./basket-discounts/basket-discounts.module').then(
        (m) => m.BasketDiscountsModule
      ),
  },
  {
    path: 'selling/prices/discounts/quantity-discounts',
    data: { sidenavForceOver: true },
    canActivateChild: [AuthenticatedGuard],
    loadChildren: () =>
      import('./quantity-discounts/quantity-discounts.module').then(
        (m) => m.QuantityDiscountsModule
      ),
  },
  { path: '**', redirectTo: 'home' },
];

const routes: Routes = [
  {
    path: 'age-verification',
    canActivateChild: [AgeUnverifiedGuard],
    loadChildren: () =>
      import('./age-verification/age-verification.module').then(
        (m) => m.AgeVerificationModule
      ),
  },
  {
    path: 'sandbox',
    canActivateChild: [DevModeGuard],
    loadChildren: () =>
      import('./sandbox/sandbox.module').then((m) => m.SandboxModule),
  },
  {
    path: '',
    canActivateChild: [AgeVerifiedGuard],
    component: LayoutContainerComponent,
    children: layoutRoutes,
  },
  { path: '**', redirectTo: '' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      enableTracing: false,
      scrollPositionRestoration: 'enabled',
      anchorScrolling: 'enabled',
      scrollOffset: () => {
        const authenticatorService = appInjector().get(AuthenticatorService);
        if (authenticatorService.route !== 'authenticated') {
          return [0, 64];
        } else {
          return [0, 0];
        }
      },
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
